import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const FILTER_PARAMS = [
  'alertSeverity',
  'alertRule',
  'alertChain',
  'alertSearch',
  'alertSort',
  'alertPage',
];

export default function useAlertFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => {
    const sortParam = searchParams.get('alertSort');
    return {
      severities: searchParams.getAll('alertSeverity'),
      rule: searchParams.get('alertRule') ?? '',
      chain: searchParams.get('alertChain') ?? '',
      search: searchParams.get('alertSearch') ?? '',
      sort: ['date', 'rule'].includes(sortParam) ? sortParam : 'severity',
      page: Math.max(1, Number.parseInt(searchParams.get('alertPage') ?? '1', 10) || 1),
    };
  }, [searchParams]);

  const updateFilters = useCallback((patch, { resetPage = true } = {}) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      const setOrDelete = (key, value) => {
        if (value === undefined) return;
        if (value === '' || value === null || value === 'severity' || value === 1) {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      };

      if (patch.severities !== undefined) {
        next.delete('alertSeverity');
        patch.severities.forEach((severity) => next.append('alertSeverity', severity));
      }
      setOrDelete('alertRule', patch.rule);
      setOrDelete('alertChain', patch.chain);
      setOrDelete('alertSearch', patch.search);
      setOrDelete('alertSort', patch.sort);
      if (patch.page !== undefined) setOrDelete('alertPage', patch.page);
      else if (resetPage) next.delete('alertPage');

      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const clearFilters = useCallback(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      FILTER_PARAMS.forEach((param) => next.delete(param));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return { filters, updateFilters, clearFilters };
}
